import { useEffect, useState } from "react";
import { Building2, CreditCard, FileText, Upload, Users, Save, CheckCircle2, Check, Infinity as InfinityIcon, AlertTriangle, LayoutTemplate as LayoutTemplateIcon, Mail, Gauge } from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/ui/PageHeader";
import { TeamManager } from "../components/settings/TeamManager";
import { MailTab } from "../components/settings/MailTab";
import { LayoutManager } from "../components/settings/LayoutManager";
import { UsageOverview } from "../components/settings/UsageOverview";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

interface PricingPackage {
  id: string;
  key: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly: number;
  maxInvoices: number | null;
  maxQuotes: number | null;
  maxExpenses: number | null;
  maxContacts: number | null;
  maxUsers: number | null;
  hasOcr: boolean;
  hasVatReport: boolean;
  hasBankLink: boolean;
  ocrCreditsPerMonth: number;
  isPopular?: boolean;
}

const tabs = [
  { id: "company", label: "Bedrijf", icon: Building2 },
  { id: "users", label: "Gebruikers", icon: Users },
  { id: "invoice", label: "Factuurvoorkeuren", icon: FileText },
  { id: "mail", label: "Mail configuratie", icon: Mail },
  { id: "subscription", label: "Abonnement", icon: CreditCard },
  { id: "usage", label: "Gebruik", icon: Gauge },
];

export default function Settings() {
  const { user, refetchUser, activeCompany, activeCompanyId } = useAuth();
  // Gebruik het actieve bedrijf uit de bedrijf-wisselaar (Topbar), NIET
  // user.companies[0] — anders negeert Instellingen de wisselaar en toont het
  // altijd hetzelfde (oudste) bedrijf, ongeacht wat de gebruiker koos.
  const company = activeCompany;
  const companyId = activeCompanyId;

  const [activeTab, setActiveTab] = useState("company");
  // Sub-tab onder "Factuurvoorkeuren": keuze tussen de voorkeuren zelf en de
  // factuur-lay-out (LayoutManager). De losse Lay-out-tab is hierin opgegaan.
  const [invoiceSubTab, setInvoiceSubTab] = useState<"prefs" | "layout">("prefs");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [companyDetail, setCompanyDetail] = useState<any>(null);

  const [name, setName] = useState(company?.name || "");
  const [kvk, setKvk] = useState("");
  const [vat, setVat] = useState("");
  const [iban, setIban] = useState("");
  const [bic, setBic] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Nederland");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [paymentTermDays, setPaymentTermDays] = useState("14");
  const [invoicePrefix, setInvoicePrefix] = useState("");
  const [invoiceNumberLength, setInvoiceNumberLength] = useState("4");
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("1");
  const [quotePrefix, setQuotePrefix] = useState("");
  const [quoteNumberLength, setQuoteNumberLength] = useState("4");
  const [nextQuoteNumber, setNextQuoteNumber] = useState("1");
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Abonnement self-service
  const [pricingPackages, setPricingPackages] = useState<PricingPackage[]>([]);
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [confirmPkg, setConfirmPkg] = useState<PricingPackage | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState("");

  useEffect(() => {
    api.get("/api/packages").then((res) => setPricingPackages(res.data));
  }, []);

  const currentPackageId = companyDetail?.packages?.[0]?.package?.id;
  const currentSortOrder = (pkgId: string) =>
    pricingPackages.find((p) => p.id === pkgId)?.maxInvoices ?? 0;

  const reloadCompany = async () => {
    if (!companyId) return;
    const { data: c } = await api.get(`/api/companies/${companyId}`);
    setCompanyDetail(c);
    setName(c.name || "");
    setKvk(c.kvkNumber || "");
    setVat(c.vatNumber || "");
    setIban(c.iban || "");
    setBic(c.bic || "");
    setAccountHolder(c.accountHolder || "");
    setAddress(c.address || "");
    setZip(c.zipCode || "");
    setCity(c.city || "");
    setCountry(c.country || "Nederland");
    setPhone(c.phone || "");
    setEmail(c.email || "");
    setWebsite(c.website || "");
    setPaymentTermDays(c.paymentTermDays ? String(c.paymentTermDays) : "14");
    setInvoicePrefix(c.invoicePrefix || "");
    setInvoiceNumberLength(c.invoiceNumberLength ? String(c.invoiceNumberLength) : "4");
    setNextInvoiceNumber(c.nextInvoiceNumber ? String(c.nextInvoiceNumber) : "1");
    setQuotePrefix(c.quotePrefix || "");
    setQuoteNumberLength(c.quoteNumberLength ? String(c.quoteNumberLength) : "4");
    setNextQuoteNumber(c.nextQuoteNumber ? String(c.nextQuoteNumber) : "1");
    setLogoPreview(c.logoUrl ? `${import.meta.env.VITE_API_URL}${c.logoUrl}` : null);
    return c;
  };

  useEffect(() => {
    reloadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSaveCompany = async () => {
    setLoading(true);
    setSuccess("");
    const formData = new FormData();
    formData.append("name", name);
    if (kvk) formData.append("kvkNumber", kvk);
    if (vat) formData.append("vatNumber", vat);
    if (iban) formData.append("iban", iban);
    if (bic) formData.append("bic", bic);
    if (accountHolder) formData.append("accountHolder", accountHolder);
    if (address) formData.append("address", address);
    if (zip) formData.append("zipCode", zip);
    if (city) formData.append("city", city);
    if (country) formData.append("country", country);
    if (phone) formData.append("phone", phone);
    if (email) formData.append("email", email);
    if (website) formData.append("website", website);
    if (paymentTermDays) formData.append("paymentTermDays", paymentTermDays);
    formData.append("invoicePrefix", invoicePrefix);
    if (invoiceNumberLength) formData.append("invoiceNumberLength", invoiceNumberLength);
    if (nextInvoiceNumber) formData.append("nextInvoiceNumber", nextInvoiceNumber);
    formData.append("quotePrefix", quotePrefix);
    if (quoteNumberLength) formData.append("quoteNumberLength", quoteNumberLength);
    if (nextQuoteNumber) formData.append("nextQuoteNumber", nextQuoteNumber);
    if (logo) formData.append("logo", logo);

    await api.patch(`/api/companies/${companyId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    await refetchUser();
    setSuccess("Bedrijfsgegevens opgeslagen.");
    setLoading(false);
  };

  const handleLogoChange = (f: File | null) => {
    setLogo(f);
    if (f) setLogoPreview(URL.createObjectURL(f));
  };

  const doSwitch = async () => {
    if (!confirmPkg || !companyId) return;
    setSwitching(true);
    setSwitchError("");
    try {
      await api.post("/api/companies/subscribe", { packageId: confirmPkg.id, companyId });
      const { data } = await api.get(`/api/companies/${companyId}`);
      setCompanyDetail(data);
      setConfirmPkg(null);
      setSuccess("Abonnement gewijzigd.");
    } catch (err: any) {
      setSwitchError(err?.response?.data?.error || "Wisselen mislukt");
    } finally {
      setSwitching(false);
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case "company":
        return (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
                <Building2 size={30} />
              </div>
              <h2 className="text-lg font-bold">Bedrijfsgegevens</h2>
            </div>

            {success && (
              <div className="mb-5 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm font-semibold">
                <CheckCircle2 size={27} /> {success}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input label="Bedrijfsnaam" value={name} onChange={(e) => setName(e.target.value)} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="KVK-nummer" value={kvk} onChange={(e) => setKvk(e.target.value)} />
                  <Input label="BTW-nummer" value={vat} onChange={(e) => setVat(e.target.value)} />
                </div>
                <Input label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} />
                <Input label="BIC" value={bic} onChange={(e) => setBic(e.target.value)} />
                <Input label="Ten name van" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} />
                <Input label="Adres" value={address} onChange={(e) => setAddress(e.target.value)} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Postcode" value={zip} onChange={(e) => setZip(e.target.value)} />
                  <Input label="Plaats" value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <Input label="Land" value={country} onChange={(e) => setCountry(e.target.value)} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Telefoon" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  <Input label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <Input label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-bold text-slate-700">Logo</label>
                <div className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 flex items-center justify-center h-36">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="max-h-28 object-contain" />
                  ) : (
                    <span className="text-sm text-slate-400">Geen logo geüpload</span>
                  )}
                </div>
                <label className="flex items-center justify-center gap-2 w-full h-14 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-brand-400 hover:bg-brand-50/50 transition-colors">
                  <Upload size={30} className="text-slate-400" />
                  <span className="text-sm text-slate-500">{logo ? logo.name : "Nieuw logo uploaden"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleLogoChange(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveCompany} loading={loading}>
                <Save size={27} /> Opslaan
              </Button>
            </div>
          </Card>
        );

      case "subscription":
        const sub = companyDetail?.packages?.[0];
        const isDowngrade = confirmPkg && currentPackageId
          ? currentSortOrder(confirmPkg.id) < currentSortOrder(currentPackageId)
          : false;
        return (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                  <CreditCard size={30} />
                </div>
                <h2 className="text-lg font-bold">Huidige abonnement</h2>
              </div>
              {sub ? (
                <div className="p-5 rounded-2xl bg-gradient-to-r from-brand-500/5 to-indigo-500/5 border border-brand-200">
                  <p className="text-2xl font-bold text-slate-800">{sub.package.name}</p>
                  <p className="text-sm text-slate-500 mt-1">Status: <span className="font-semibold text-emerald-600">{sub.status}</span></p>
                  <p className="text-sm text-slate-500">Gestart op {new Date(sub.startDate).toLocaleDateString("nl-NL")}</p>
                </div>
              ) : (
                <p className="text-slate-500">Geen actief abonnement gevonden.</p>
              )}
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-800">Wijzig pakket</h3>
                <div className="inline-flex items-center bg-white/70 rounded-full p-1 border border-slate-200">
                  <button
                    onClick={() => setBilling("monthly")}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${billing === "monthly" ? "bg-brand-500 text-white" : "text-slate-600"}`}
                  >
                    Maandelijks
                  </button>
                  <button
                    onClick={() => setBilling("yearly")}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${billing === "yearly" ? "bg-brand-500 text-white" : "text-slate-600"}`}
                  >
                    Jaarlijks
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-5">
                Je wisselt het abonnement voor je hele bedrijf. Excl. btw.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                {pricingPackages.map((p) => {
                  const isCurrent = p.id === currentPackageId;
                  return (
                    <div
                      key={p.id}
                      className={`relative p-5 rounded-2xl border flex flex-col transition-all ${
                        isCurrent
                          ? "border-brand-500 bg-brand-50/40"
                          : "border-slate-200 bg-white hover:shadow-md"
                      }`}
                    >
                      {isCurrent && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                          Huidig
                        </span>
                      )}
                      <h4 className="font-bold text-slate-800">{p.name}</h4>
                      {p.description && <p className="text-xs text-slate-500 mt-1 min-h-[32px]">{p.description}</p>}
                      <div className="my-3">
                        <span className="text-2xl font-extrabold text-gradient">
                          €{billing === "monthly" ? p.priceMonthly.toFixed(2) : p.priceYearly.toFixed(2)}
                        </span>
                        <span className="text-slate-500 text-xs">/{billing === "monthly" ? "maand" : "jaar"}</span>
                      </div>
                      <ul className="space-y-1.5 mb-4 flex-1 text-xs text-slate-600">
                        <li className="flex items-center gap-1.5">
                          <Check size={21} className="text-brand-500 shrink-0" />
                          {p.maxInvoices === null ? <InfinityIcon size={18} /> : p.maxInvoices} facturen/maand
                        </li>
                        <li className="flex items-center gap-1.5">
                          <Check size={21} className="text-brand-500 shrink-0" />
                          {p.maxUsers === null ? <InfinityIcon size={18} /> : p.maxUsers} gebruikers
                        </li>
                        {p.hasVatReport && (
                          <li className="flex items-center gap-1.5">
                            <Check size={21} className="text-brand-500 shrink-0" /> BTW-overzicht
                          </li>
                        )}
                        {p.hasOcr && (
                          <li className="flex items-center gap-1.5">
                            <Check size={21} className="text-brand-500 shrink-0" /> {p.ocrCreditsPerMonth} uitgaven-bon/maand lezen
                          </li>
                        )}
                      </ul>
                      <Button
                        variant={isCurrent ? "outline" : "primary"}
                        size="sm"
                        className="w-full"
                        disabled={isCurrent}
                        onClick={() => {
                          setSwitchError("");
                          setConfirmPkg(p);
                        }}
                      >
                        {isCurrent ? "Actief" : "Kies dit pakket"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Bevestigingsmodal */}
            <Modal
              isOpen={!!confirmPkg}
              onClose={() => setConfirmPkg(null)}
              title="Pakket wijzigen"
              size="sm"
              footer={
                <div className="flex justify-end gap-3">
                  <Button variant="ghost" onClick={() => setConfirmPkg(null)} disabled={switching}>
                    Annuleren
                  </Button>
                  <Button variant="primary" onClick={doSwitch} loading={switching}>
                    Bevestig wijziging
                  </Button>
                </div>
              }
            >
              <div className="space-y-3">
                {isDowngrade && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle size={27} className="shrink-0 mt-0.5" />
                    <span>
                      Je downgradet naar een pakket met minder mogelijkheden. Sommige functies
                      kunnen beperkt worden.
                    </span>
                  </div>
                )}
                <p className="text-sm text-slate-600">
                  Wijzig het abonnement van je bedrijf naar <strong>{confirmPkg?.name}</strong>?
                  Het vorige abonnement wordt beëindigd en het nieuwe start direct.
                </p>
                {switchError && (
                  <p className="text-sm text-red-600">{switchError}</p>
                )}
              </div>
            </Modal>
          </div>
        );

      case "invoice":
        return (
          <div className="space-y-4">
            {/* Sub-tab-keuze binnen Factuurvoorkeuren: de voorkeuren zelf of de
                factuur-lay-out (LayoutManager). De voormalige losse Lay-out-tab
                is hierin opgegaan. */}
            <div className="flex gap-2">
              <button
                onClick={() => setInvoiceSubTab("prefs")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  invoiceSubTab === "prefs"
                    ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-md shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <FileText size={18} /> Factuurvoorkeuren
              </button>
              <button
                onClick={() => setInvoiceSubTab("layout")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  invoiceSubTab === "layout"
                    ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-md shadow-brand-500/20"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <LayoutTemplateIcon size={18} /> Factuur Lay-out
              </button>
            </div>

            {invoiceSubTab === "prefs" ? (
              <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <FileText size={30} />
              </div>
              <h2 className="text-lg font-bold">Factuurvoorkeuren</h2>
            </div>
            <div className="max-w-md space-y-4">
              <Input
                label="Standaard betalingstermijn (dagen)"
                type="number"
                value={paymentTermDays}
                onChange={(e) => setPaymentTermDays(e.target.value)}
              />

              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-bold text-slate-700 mb-1">Factuurnummering</p>
                <p className="text-xs text-slate-500 mb-3">
                  Stel een eigen prefix, aantal cijfers en startnummer in. Laat de prefix leeg om de
                  standaard jaar-indeling (bijv. 20260001) te blijven gebruiken.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Prefix"
                    placeholder="bijv. FA of AL-"
                    value={invoicePrefix}
                    onChange={(e) => setInvoicePrefix(e.target.value)}
                  />
                  <Input
                    label="Aantal cijfers"
                    type="number"
                    value={invoiceNumberLength}
                    onChange={(e) => setInvoiceNumberLength(e.target.value)}
                  />
                </div>
                <div className="mt-3">
                  <Input
                    label="Startnummer (begin cijfer)"
                    type="number"
                    value={nextInvoiceNumber}
                    onChange={(e) => setNextInvoiceNumber(e.target.value)}
                  />
                </div>

                {/* Live voorbeeld */}
                {(() => {
                  const len = Math.max(1, parseInt(invoiceNumberLength) || 4);
                  const start = Math.max(1, parseInt(nextInvoiceNumber) || 1);
                  const preview = invoicePrefix
                    ? `${invoicePrefix}${start.toString().padStart(len, "0")}`
                    : `${new Date().getFullYear()}${start.toString().padStart(len, "0")}`;
                  const nextN = start + 1;
                  const nextPreview = invoicePrefix
                    ? `${invoicePrefix}${nextN.toString().padStart(len, "0")}`
                    : `${new Date().getFullYear()}${nextN.toString().padStart(len, "0")}`;
                  return (
                    <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">Voorbeeld:</p>
                      <p className="font-mono text-lg font-bold text-slate-800">
                        {preview} <span className="text-slate-400 text-sm font-sans">→ volgende:</span>{" "}
                        <span className="font-mono text-base text-slate-600">{nextPreview}</span>
                      </p>
                    </div>
                  );
                })()}
                <p className="mt-2 text-[0.6875rem] text-slate-400">
                  Let op: het startnummer bepaalt het volgende factuurnummer. Verhoog dit alleen als
                  je weet wat je doet — bestaande facturen behouden hun nummer.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-bold text-slate-700 mb-1">Offertenummering</p>
                <p className="text-xs text-slate-500 mb-3">
                  Stel een eigen prefix, aantal cijfers en startnummer in. Laat de prefix leeg om de
                  standaard jaar-indeling (bijv. OFF-2026-0001) te blijven gebruiken.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Prefix"
                    placeholder="bijv. OFF of QU-"
                    value={quotePrefix}
                    onChange={(e) => setQuotePrefix(e.target.value)}
                  />
                  <Input
                    label="Aantal cijfers"
                    type="number"
                    value={quoteNumberLength}
                    onChange={(e) => setQuoteNumberLength(e.target.value)}
                  />
                </div>
                <div className="mt-3">
                  <Input
                    label="Startnummer (begin cijfer)"
                    type="number"
                    value={nextQuoteNumber}
                    onChange={(e) => setNextQuoteNumber(e.target.value)}
                  />
                </div>

                {/* Live voorbeeld */}
                {(() => {
                  const len = Math.max(1, parseInt(quoteNumberLength) || 4);
                  const start = Math.max(1, parseInt(nextQuoteNumber) || 1);
                  const preview = quotePrefix
                    ? `${quotePrefix}${start.toString().padStart(len, "0")}`
                    : `OFF-${new Date().getFullYear()}-${start.toString().padStart(len, "0")}`;
                  const nextN = start + 1;
                  const nextPreview = quotePrefix
                    ? `${quotePrefix}${nextN.toString().padStart(len, "0")}`
                    : `OFF-${new Date().getFullYear()}-${nextN.toString().padStart(len, "0")}`;
                  return (
                    <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">Voorbeeld:</p>
                      <p className="font-mono text-lg font-bold text-slate-800">
                        {preview} <span className="text-slate-400 text-sm font-sans">→ volgende:</span>{" "}
                        <span className="font-mono text-base text-slate-600">{nextPreview}</span>
                      </p>
                    </div>
                  );
                })()}
                <p className="mt-2 text-[0.6875rem] text-slate-400">
                  Let op: het startnummer bepaalt het volgende offertenummer. Verhoog dit alleen als
                  je weet wat je doet — bestaande offertes behouden hun nummer.
                </p>
              </div>

              <Button onClick={handleSaveCompany} loading={loading}>Voorkeuren opslaan</Button>
            </div>
          </Card>
            ) : (
              <LayoutManager
                companyId={companyId || ""}
                company={companyDetail}
                isSuperadmin={user?.role === "SUPERADMIN"}
              />
            )}
          </div>
        );

      case "mail":
        return <MailTab companyId={companyId || undefined} />;

      case "usage":
        return <UsageOverview companyDetail={companyDetail} />;

      case "users":
        return (
          <TeamManager
            companyId={companyId || undefined}
            members={companyDetail?.members || []}
            maxUsers={
              // Superadmin valt niet onder de pakket-limiet (maxUsers): mag
              // altijd teamleden toevoegen, ongeacht het pakket van het bedrijf.
              user?.role === "SUPERADMIN"
                ? null
                : (companyDetail?.packages?.find((p: any) => p.status === "ACTIVE")?.package?.maxUsers ?? null)
            }
            ownerId={companyDetail?.ownerId || ""}
            currentUserId={user?.id || ""}
            onReload={reloadCompany}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Instellingen" subtitle="Beheer je bedrijfsprofiel en voorkeuren" />

      <Card className="p-2">
        <div className="flex gap-2 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                activeTab === t.id
                  ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/20"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <t.icon size={24} />
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      {renderTab()}
    </div>
  );
}
